import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { inventoryApi } from "../api";
import { InventoryPageHeader } from "../components/InventoryPageHeader";
import { InventoryError, InventoryLoading } from "../components/InventoryState";
import { InventoryStatusBadge } from "../components/InventoryStatusBadge";
import { StockAdjustmentForm } from "../components/StockAdjustmentForm";
import { useAsyncData, useMutation } from "../hooks";
import type { InventoryItem, InventoryItemAdjustmentRequest } from "../types";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function movementLabel(type: string) {
  return type.replaceAll("_", " ");
}

export default function InventoryItemViewPage() {
  const id = Number(useParams().id);
  const { data, loading, error, reload } = useAsyncData(() => inventoryApi.getItem(id), [id]);
  const adjustment = useMutation<InventoryItemAdjustmentRequest, InventoryItem>(
    (payload) => inventoryApi.adjustItem(id, payload),
  );

  if (loading) return <InventoryLoading label="Loading inventory item..." />;
  if (error) return <InventoryError message={error} />;
  if (!data) return <InventoryError message="Inventory item not found." />;

  return (
    <div className="space-y-5">
      <InventoryPageHeader
        title={data.name}
        description={`Item Code: ${data.code}`}
        actions={
          <div className="flex gap-2">
            <Link to="/inventory/items" className="rounded-xl border px-4 py-2 text-sm font-semibold">
              ← Back
            </Link>
            <Link to={`/inventory/items/${data.id}/edit`} className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
              Edit
            </Link>
          </div>
        }
      />

      {data.belowReorderLevel && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm font-semibold text-amber-800">
          ⚠ This item is below its reorder level. Current issue stock:{" "}
          <span className="font-bold">{Number(data.currentStockUnits || 0).toLocaleString()} {data.issueUnitName}</span>
          {data.reorderLevelInIssueUnits != null && (
            <> (reorder level: {data.reorderLevelInIssueUnits} {data.issueUnitName})</>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Issue Stock" value={`${Number(data.currentStockUnits || 0).toLocaleString()} ${data.issueUnitName}`} />
        <Card label="Stock Unit Balance" value={`${Number(data.currentStockInStockUnits || 0).toLocaleString()} ${data.stockUnitName}`} />
        <Card label="Reorder Level" value={data.reorderLevelInIssueUnits != null ? `${data.reorderLevelInIssueUnits} ${data.issueUnitName}` : "Not set"} />
        <Card label="Max Level" value={data.maximumLevelInIssueUnits != null ? `${data.maximumLevelInIssueUnits} ${data.issueUnitName}` : "No limit"} />
      </div>

      {/* Details */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-slate-900">Item Details</h2>
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <Detail label="Item Code" value={data.code} />
          <Detail label="Item Name" value={data.name} />
          <Detail label="Category" value={data.categoryName || "-"} />
          <Detail label="Status" value={<InventoryStatusBadge value={data.status} />} />
          <Detail
            label="Conversion Factor"
            value={`1 ${data.stockUnitName} = ${data.conversionFactor} ${data.issueUnitName}`}
          />
          <Detail label="Description" value={data.description || "-"} />
        </div>
      </div>

      {adjustment.error && <InventoryError message={adjustment.error} />}
      <StockAdjustmentForm
        submitting={adjustment.loading}
        onSubmit={async (payload) => {
          await adjustment.mutate(payload);
          await reload();
        }}
      />

      <MovementHistory itemId={id} />
    </div>
  );
}

function MovementHistory({ itemId }: { itemId: number }) {
  const [year, setYear] = useState<number | "">("");
  const [month, setMonth] = useState<number | "">("");
  const [page, setPage] = useState(0);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const { data, loading, error } = useAsyncData(
    () => inventoryApi.listMovements({
      itemId,
      year: year || undefined,
      month: year ? (month || undefined) : undefined,
      page,
      size: 10,
    }),
    [itemId, year, month, page],
  );

  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div>
          <h2 className="font-bold text-slate-900">Movement History</h2>
          <p className="text-xs text-slate-500">Restocks, issues, adjustments and corrections for this item</p>
        </div>
        <div className="flex gap-2">
          <select
            className="input w-32"
            value={year}
            onChange={(e) => { setPage(0); setYear(e.target.value ? Number(e.target.value) : ""); setMonth(""); }}
          >
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            className="input w-36"
            value={month}
            disabled={!year}
            onChange={(e) => { setPage(0); setMonth(e.target.value ? Number(e.target.value) : ""); }}
          >
            <option value="">All months</option>
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="p-5"><InventoryLoading label="Loading movement history..." /></div>}
      {error && <div className="p-5"><InventoryError message={error} /></div>}

      {!loading && !error && data && (
        data.content.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500">No stock movements recorded for this period.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Type</th>
                    <th className="px-5 py-3">Quantity</th>
                    <th className="px-5 py-3">Balance After</th>
                    <th className="px-5 py-3">By</th>
                    <th className="px-5 py-3">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.content.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-slate-500 whitespace-nowrap">
                        {new Date(m.occurredAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-700">{movementLabel(m.movementType)}</td>
                      <td className={`px-5 py-3 font-semibold ${m.quantityUnits >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {m.quantityUnits >= 0 ? "+" : ""}{m.quantityUnits.toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{m.balanceAfterUnits.toLocaleString()}</td>
                      <td className="px-5 py-3 text-slate-500">{m.actedByUsername ?? "—"}</td>
                      <td className="px-5 py-3 text-slate-500 max-w-xs truncate">{m.remarks ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-5 py-3 text-xs text-slate-500">
              <span>Page {page + 1} of {Math.max(data.totalPages, 1)} · {data.totalElements} movements</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-lg border px-2.5 py-1 font-semibold disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={data.last}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border px-2.5 py-1 font-semibold disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

function Card({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-1 font-medium text-slate-800">{value}</div>
    </div>
  );
}
